'use strict';

import { action, flow, observable, configure, makeObservable } from "mobx";
import React from 'react';
configure({enforceActions: 'always'});

import appStore from '@uw-it-sis/lib-react/lib/AppStore';
import {States} from '@uw-it-sis/lib-react/lib/AppConstants';

import {filter, find, each} from 'underscore';
import { sprintf } from 'sprintf-js';
import {isEmpty} from '@uw-it-sis/lib-react/lib/Utils';
import TermUtils from '@uw-it-sis/lib-react/lib/TermUtils';
import FacetsStore from '@uw-it-sis/lib-react-myplan/lib/FacetsStore';
import GenEdUtils from '@uw-it-sis/lib-react-myplan/lib/GenEdUtils';

import courseSearchStore from './CourseSearchStore';
import {MetricLabels} from "@uw-it-sis/lib-react-myplan/lib/Constants";


/*
 * Configuration for facets.
 */
const COURSE_MODALITY_1 = "In-person";
const facetConfigs = {
    idPropertyName: "id",
    facets: [
        {
            id: "genEdRequirements",
            label: "Gen Ed Requirements",
            analyticsKey: "Gen-Ed",
            valueFinderMethod: function (course) {
                let genEds = course.genEduReqs;
                if (genEds.length === 0) {
                    return ("");
                }
                //  Format the gened codes in a predictable way and add delimiters.
                return GenEdUtils.makeGenEdString(genEds, GenEdUtils.DELIMITER.WORDS);
            }
        },
        {
            id: "credits",
            label: "Credits",
            analyticsKey: "Credits",
            valueFinderMethod: function (course) {
                let credits = [];
                let cutoff = 5;

                let creditsString = course.credit;
                if (creditsString === "1-25") {
                    credits.push(creditsString);
                } else {
                    //  Put any credit values under "cutoff" in individually
                    let individuals = filter(course.allCredits, function (credit) {
                        if (Number(credit) <= cutoff) {
                            return (credit);
                        }
                    });
                    credits = credits.concat(individuals);

                    //  If there are any over the cutoff make an entry for the overage bucket.
                    let over = find(course.allCredits, function (credit) {
                        if (Number(credit) > cutoff) {
                            return (credit);
                        }
                    });

                    if (over) {
                        credits = credits.concat(">" + cutoff);
                    }
                }

                return (credits);
            },
            filterSortMethod: function (a, b) {
                let aIsDigit = ! isNaN(a.key);
                let bIsDigit = ! isNaN(b.key);
                // Handle sorting single number filters with a simple sort on the key.
                if (aIsDigit && bIsDigit) {
                    if (a.key < b.key) {
                        return (-1);
                    }
                    if (a.key > b.key) {
                        return (1);
                    }
                    return (0);
                } else {
                    //  Handle one-offs like ">7" or "1-25" here
                    if (aIsDigit) {
                        return (-1);
                    }
                    if (bIsDigit) {
                        return (1);
                    }

                    if (a.key === "1-25") {
                        return (1);
                    }

                    return (-1);
                }
            }
        },
        {
            id: "level",
            label: "Level",
            analyticsKey: "Level",
            valueFinderMethod: function (course) {
                return course.level;
            }
        },
        {
            id: "term",
            label: "Quarter Offered",
            analyticsKey: "Term",
            valueFinderMethod: function (course) {
                if (course.termId) {
                    return (TermUtils.makeTermDescriptionFromTermId(course.termId, TermUtils.SIZE.LONG));
                } else {
                    return ("");
                }
            }
        },
        {
            id: "online", // id: "delivery",
            label: "Learning Format", // label: "In-Person or Remote Courses",
            analyticsKey: "Online", // analyticsKey: "Delivery",
            valueFinderMethod: function (course) {
                /*
                let nonRemote = ["In-person instruction", "To be arranged"];
                if (course.onlineLearningCodes.length && nonRemote.some(c => course.onlineLearningCodes.indexOf(c) >= 0)) {
                    return "At least one in-person or TBA section";
                } else {
                    return "Only remote sections";
                }
                */

                if (course.onlineLearningCodes.length) {
                    let online = [];
                    course.onlineLearningCodes.map(function (code) {
                        online.push(code);
                    });
                    return online;
                } else {
                    return COURSE_MODALITY_1;
                }
            },
            filterSortMethod: function (a, b) {
              /*
              if(a.key < b.key) { return -1; }
              if(a.key > b.key) { return 1; }
              return 0;
              */

                if (a.key === b.key)  {
                    return 0;
                }
                if (a.key === COURSE_MODALITY_1) {
                    return -1;
                }
                if (b.key === COURSE_MODALITY_1) {
                    return 1;
                }
            },
            helpContent: (
               /* <>
                    <p><strong>At least one in-person or TBA section:</strong> Select this option to see courses that include at least one section that is held in person and/or for which instruction locations are to be arranged (TBA). You may see a combination of in-person, TBA, and/or remote sections for each course.</p>
                    <p className="mb-0"><strong>Only remote sections:</strong> Select this option to see courses in which all sections are online (remote learning). This will not include any courses that have a combination of remote and in-person/TBA sections.</p>
                </> */

                <>
                    <p className="mb-0"><strong>In-person:</strong> All required class meetings occur in a physical classroom. The course may also have online content and activities that students engage outside of class time as homework or study materials</p>
                    <p><strong>Hybrid:</strong> Some, but not all, required class meetings occur in a physical classroom. When not meeting in the physical classroom, students and instructors will attend class online, either through required synchronous sessions or required asynchronous activities. The time schedule contains information about the course’s required weekly physical classroom and synchronous meeting time</p>
                    <p><strong>Synchronous Online:</strong> Students and instructors interact online synchronously through regularly-scheduled, required meetings using applications such as Zoom. Students should also expect to interact with others and engage course materials asynchronously. The time schedule contains information about the course’s regular, required synchronous meeting time</p>
                    <p><strong>Asynchronous Online:</strong> Students and instructors always interact with others and engage course materials asynchronously. While there are no regularly-scheduled, required synchronous class meetings, students are expected to meet all assignment deadlines and may be asked to schedule occasional, brief synchronous one-on-one check-in meetings with the instructors</p>

                </>
            )
        },
        {
            id: "grading",
            label: "Grading System",
            analyticsKey: "Grading",
            valueFinderMethod: (course) => {
                if (course.hasOwnProperty("gradingSystems") && course.gradingSystems.length) {
                    let gradingSystems = [];
                    course.gradingSystems.map(function (code) {
                        let option = code.split(/\b/g).map(x => {
                            return x.charAt(0).toUpperCase() + x.slice(1);
                        }).join("");
                        gradingSystems.push(option);
                    });
                    return gradingSystems;
                } else {
                    return ("");
                }
            }
        }
    ]
};

const sortMethods = {
    /* Descending sort on score then course code */
    relevance: function (a, b) {
        if (a.score > b.score) {
            return -1;
        }
        if (a.score < b.score) {
            return 1;
        }
        // a must be equal to b, so look at course code
        let result = sortMethods.courseCode(a, b);
        if (result !== 0) {
            return result;
        }

        //  Still equal, look at term.
        if (a.termId < b.termId) {
            return -1;
        }
        if (a.termId > b.termId) {
            return 1;
        }

        return 0;
    },
    /* Ascending sort on course code */
    courseCode: function (a, b) {
        if (a.code < b.code) {
            return -1;
        }
        if (a.code > b.code) {
            return 1;
        }

        //  Still equal, look at term.
        if (a.termId < b.termId) {
            return -1;
        }
        if (a.termId > b.termId) {
            return 1;
        }
        // Still equal (this shouldn't happen)
        return 0;
    },
    courseTitle: function (a, b) {
        if (a.title < b.title) {
            return -1;
        }
        if (a.title > b.title) {
            return 1;
        }
        // a must be equal to b so do a sub-sort on code.
        return sortMethods.courseCode(a, b);
    }
};

/**
 * Store for course search results panel.
 */
class CourseSearchResultsStore {

    state = States.initial;

    // This is persistent data.
    data;

    constructor() {
        makeObservable(this, {
            state: observable,
            data: observable,
            mount: action
        });

        this.componentKey = "CourseSearchResults";

        this.allCourses = [];
        this.filteredCourses = [];
        this.actionPendingText = "";

        this.facetsStore = new FacetsStore(facetConfigs);

        // Register the store for URL persistence.
        this.urlPersistenceStore = appStore.getUrlPersistenceStore();
        this.urlPersistenceStore.register(this.componentKey, this.defaultData);

        this.analyticsStore = appStore.getAnalyticsStore();
    }

    /**
     * Initialize the component. This gets called on every search.
     */
    mount(courses, newSearch) {
        // Look for data in the URL persistence.
        const data = this.urlPersistenceStore.getPersistedData(this.componentKey);

        if (data == null || newSearch) {
            // If the component state state wasn't available from URL persistence.
            // Or the user fired a fresh search
            this.data = this.defaultData();
        } else {
            // Otherwise, use the persisted data
            this.data = data;
        }

        this.allCourses = courses;
        this.filteredCourses = this.allCourses;
        //Filter the initial result set on (primary: relevancy score, secondary: courseCode, courseTitle)
        this.filteredCourses.sort(sortMethods[this.data.sortMode]);
        // Build facets.
        this.facetsStore.buildFilters(this.allCourses);

        // Sets the filter states on the facets.
        if (! isEmpty(this.data.activeFilters)) {
            this.facetsStore.setActiveFacetFilterModels(this.data.activeFilters);
            this._filter();
        }

        this.state = States.done;
    }

    /**
     * This is the starting state if no state is found in persistence.
     */
    defaultData() {
        return {
            activeFilters: {},
            sortMode: "relevance",
            paginationActivePage: 0
        };
    }

    /**
     * This is used to clear/reset the filters on every new search triggered by a user.
     */
    resetFilters() {
        this.data.activeFilters = {};
        this.data.sortMode = "relevance";
        this.data.paginationActivePage = 0;
        this.urlPersistenceStore.pushComponentDataToUrl(this.componentKey, this.data);
    }

    /**
     * This is used to update the results to show a certain subset of the results as a whole
     */
    pageChange = flow(function * (pageNumber) {
        if (pageNumber !== this.data.paginationActivePage) {
            // Block the UI
            this.state = States.pending;
            // Let the UI update
            yield new Promise(resolve => resolve()).then();
            // Do the work.
            this.data.paginationActivePage = pageNumber;
            this.urlPersistenceStore.pushComponentDataToUrl(this.componentKey, this.data);
            this.state = States.done;
        }
    }.bind(this));

    /**
     * Updates the sort order of search results.
     *
     * @param value The value of the sort widget.
     * @param displayValue The text to be used in the blocking message.
     */
    sortResults = flow(function * (value, displayValue) {
        // Block the UI
        this.state = States.pending;

        this.actionPendingText = sprintf("Sorting by %s ...", displayValue);
        // Set the sort mode.
        this.data.sortMode = value;
        // Create an analytics action
        const labels = this.analyticsStore.label(MetricLabels.sortByName, value).get();
        this.analyticsStore.addEvent("Search-Keyword-Sort", labels, {});

        /*
         * The method returns (generator-style) right here.
         * Since an observable (this.state) was updated above render() gets called on the UI component, and the
         * component will be blocked with the loading message.
         */
        yield new Promise(resolve => resolve()).then();

        /*
         * Here's where the labor intensive work happens.
         */
        const sortMethod = sortMethods[this.data.sortMode];
        this.filteredCourses.sort(sortMethod);
        // Reset the pagination back to the first page.
        this.data.paginationActivePage = 0;
        this.actionPendingText = "";
        this.urlPersistenceStore.pushComponentDataToUrl(this.componentKey, this.data);

        // Unblock the UI
        this.state = States.done;
     }.bind(this));

    /**
     * Applies filters to all courses and updates the filtered courses list.
     */
    _filter() {
        this.filteredCourses = this.facetsStore.filter(this.allCourses);
        this.filteredCourses.sort(sortMethods[this.state.sortMode]);
    }

    /**
     * Turns off all filters for a particular facet.
     */
    resetFacet = flow(function * (facetId) {
        // Block the UI
        this.state = States.pending;
        // ... and let the UI update
        yield new Promise(resolve => resolve()).then();

        // Do the work.
        this.facetsStore.clearFacet(facetId);
        this._resolveFacetUpdate();
    }.bind(this));

    /**
     * Changes the state of one or more facet filters.
     */
    toggleFacetFilters = flow(function * (filterIds) {
        // Block the UI
        this.state = States.pending;
        // Let the UI update
        yield new Promise(resolve => resolve()).then();
        // Do the work down in the facets store.

        // Update the filter state(s)
        this.facetsStore.changeFilters(filterIds);
        this._resolveFacetUpdate();
    }.bind(this));

    /**
     * Clears all facet filters.
     */
    resetAllFacets = flow(function * () {
        // Block the UI
        this.state = States.pending;
        // ... and let the UI update
        yield new Promise(resolve => resolve()).then();

        // Do the work.
        this.facetsStore.setAllFiltersActive(true);
        // Resolve.
        this._resolveFacetUpdate();
    }.bind(this));

    /**
     * Common stuff to conclude any facet update.
     * @private
     */
    _resolveFacetUpdate() {
        // Filter the result set, update the filtered courses list, and sort.
        this._filter();

         // Set the activeFilters to the state and push it to the url.
        this.data.activeFilters = this.facetsStore.getActiveFacetFilterModels();
        this.urlPersistenceStore.pushComponentDataToUrl(this.componentKey, this.data);

        // Reset pagination to first the page
        this.data.paginationActivePage = 0;

        // Pass filter data to the CDP so that they can be applied (e.g. term == fall).
        this._shareFacetFilterValues();

        this.state = States.done;
    }

    /**
     * Writes active filters data up to the course search store.
     * @private
     */
    _shareFacetFilterValues() {
        // This doesn't actually return the whole model, just the id.
        let activeFilters = this.facetsStore.getActiveFacetFilterModels();
        let filtersObj = {};
        // See if the term facet is in play.
        if (activeFilters['term']) {
            // If it is then convert the filter value to a proper term id.
            filtersObj['terms'] = [];
            each(activeFilters['term'], function (filterId) {
                let termId = TermUtils.makeTermIdFromTermName(filterId);
                if (termId) {
                    filtersObj['terms'].push(termId);
                }
            });
        }
        // Update the course search store.
        courseSearchStore.updateActiveFilters(filtersObj);
    }

    /**
     * Getter for filtered, paginated search results
     */
    getFilteredSearchResults(numberOfResults) {
        let startIndex = this.data.paginationActivePage * numberOfResults;
        let endIndex = startIndex + numberOfResults;
        //(this.data.paginationActivePage - 1) * numberOfResults;
        //()
        return this.filteredCourses.slice(startIndex, endIndex);
    }

    /**
     * A pass-thru to the FacetsStore.
     * @return {*} A data structure which contains FacetModels.
     */
    getFacetModels() {
        return this.facetsStore.getFacetModels();
    }

    /**
     * Getter for pagination results range.
     */
    getFilteredSearchResultsPaginationRange(numberOfResults) {
        if (this.getFilteredSearchResultsCount() < numberOfResults) {
            return this.getFilteredSearchResultsCount() + " result" + (this.getFilteredSearchResultsCount() !== 1 ? 's':'');
        } else {
            let firstIndex = (this.data.paginationActivePage * numberOfResults);
            let first = firstIndex + 1;
            let last = firstIndex + numberOfResults;
            if (last > this.getFilteredSearchResultsCount()) last = this.getFilteredSearchResultsCount();
            return first.toLocaleString() + "-" + last.toLocaleString() + " of " + this.getFilteredSearchResultsCount() + " results";
        }
    }

    getAllSearchResultsCount() {
        return this.allCourses.length;
    }

    getFilteredSearchResultsCount() {
        return this.filteredCourses.length;
    }
}

export default new CourseSearchResultsStore();
